import React, { useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "../config/api";

const EMPTY_FILTER = {
  min: 0,
  max: Number.MAX_SAFE_INTEGER,
  categoryId: null,
  subCategoryId: null,
  cityId: null,
};

const FilterModal = ({ isOpen, onClose, onApply, activeFilter }) => {
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [selectedCityId, setSelectedCityId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [citySearch, setCitySearch] = useState("");
  const [showCityList, setShowCityList] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMinPrice(activeFilter?.min ? String(activeFilter.min) : "");
    setMaxPrice(activeFilter?.max && activeFilter.max !== Number.MAX_SAFE_INTEGER ? String(activeFilter.max) : "");
    setSelectedCategoryId(activeFilter?.categoryId ?? null);
    setSelectedSubCategoryId(activeFilter?.subCategoryId ?? null);
    setSelectedCityId(activeFilter?.cityId ?? null);
    setCitySearch("");
    setShowCityList(false);
  }, [isOpen, activeFilter]);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    Promise.all([
      fetch(`${API_BASE_URL}/Category/GetCategories`).then((r) => r.json()).catch(() => []),
      fetch(`${API_BASE_URL}/SubCategory/GetSubCategory`).then((r) => r.json()).catch(() => []),
      fetch(`${API_BASE_URL}/City/GetCities`).then((r) => r.json()).catch(() => []),
    ])
      .then(([cats, subs, cts]) => {
        if (Array.isArray(cats)) setCategories(cats);
        if (Array.isArray(subs)) setSubCategories(subs);
        if (Array.isArray(cts)) setCities(cts);
      })
      .finally(() => setLoading(false));
  }, [isOpen]);

  const filteredSubCategories = useMemo(() => {
    if (!selectedCategoryId) return [];
    return subCategories.filter((s) => String(s.CategoryId ?? s.categoryId) === String(selectedCategoryId));
  }, [subCategories, selectedCategoryId]);

  const filteredCities = useMemo(() => {
    const term = citySearch.trim().toLowerCase();
    if (!term) return cities;
    return cities.filter((c) => String((c.Name ?? c.name) || "").toLowerCase().includes(term));
  }, [cities, citySearch]);

  const hasActiveFilters = () => {
    const min = minPrice ? parseInt(minPrice, 10) : 0;
    const max = maxPrice ? parseInt(maxPrice, 10) : Number.MAX_SAFE_INTEGER;
    return !(min === 0 && max === Number.MAX_SAFE_INTEGER && !selectedCategoryId && !selectedSubCategoryId && !selectedCityId);
  };

  const handleApply = () => {
    onApply({
      min: minPrice ? parseInt(minPrice, 10) : 0,
      max: maxPrice ? parseInt(maxPrice, 10) : Number.MAX_SAFE_INTEGER,
      categoryId: selectedCategoryId,
      subCategoryId: selectedSubCategoryId,
      cityId: selectedCityId,
    });
    onClose();
  };

  const resetAll = () => {
    setMinPrice("");
    setMaxPrice("");
    setSelectedCategoryId(null);
    setSelectedSubCategoryId(null);
    setSelectedCityId(null);
    setCitySearch("");
    setShowCityList(false);
    onApply({ ...EMPTY_FILTER });
    onClose();
  };

  if (!isOpen) return null;

  const chip = (active) => ({
    padding: "8px 14px",
    borderRadius: "999px",
    border: active ? "1px solid #2563eb" : "1px solid #e2e8f0",
    background: active ? "#2563eb" : "#fff",
    color: active ? "#fff" : "#334155",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
  });

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)",
        zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", width: "100%", maxWidth: "560px", maxHeight: "88vh",
          borderRadius: "18px 18px 0 0", display: "flex", flexDirection: "column",
          boxShadow: "0 -8px 30px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px" }}>
          <div style={{ width: "44px", height: "4px", borderRadius: "2px", background: "#cbd5e1" }} />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px 10px" }}>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 800, color: "#0f172a" }}>Filter Products</h3>
          <button
            onClick={onClose}
            style={{ border: "none", background: "#f1f5f9", width: "32px", height: "32px", borderRadius: "50%", cursor: "pointer", fontSize: "15px", color: "#64748b" }}
          >
            ✕
          </button>
        </div>

        <div style={{ overflowY: "auto", padding: "0 20px 10px", flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#64748b", fontWeight: 600 }}>
              Filter options load ho rahe hain...
            </div>
          ) : (
            <>
              <div style={{ marginBottom: "18px" }}>
                <div style={label}>Price Range (₹)</div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <input
                    value={minPrice}
                    onChange={(e) => setMinPrice(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="From (0)"
                    inputMode="numeric"
                    style={input}
                  />
                  <input
                    value={maxPrice}
                    onChange={(e) => setMaxPrice(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="To (Koi bhi)"
                    inputMode="numeric"
                    style={input}
                  />
                </div>
              </div>

              <div style={{ marginBottom: "18px" }}>
                <div style={label}>Category</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  <button style={chip(!selectedCategoryId)} onClick={() => { setSelectedCategoryId(null); setSelectedSubCategoryId(null); }}>
                    Sabhi
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.Id ?? cat.id}
                      style={chip(String(selectedCategoryId) === String(cat.Id ?? cat.id))}
                      onClick={() => { setSelectedCategoryId(cat.Id ?? cat.id); setSelectedSubCategoryId(null); }}
                    >
                      {cat.Name ?? cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {selectedCategoryId && filteredSubCategories.length > 0 && (
                <div style={{ marginBottom: "18px" }}>
                  <div style={label}>Sub Category</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                    <button style={chip(!selectedSubCategoryId)} onClick={() => setSelectedSubCategoryId(null)}>
                      Sabhi
                    </button>
                    {filteredSubCategories.map((sub) => (
                      <button
                        key={sub.Id ?? sub.id}
                        style={chip(String(selectedSubCategoryId) === String(sub.Id ?? sub.id))}
                        onClick={() => setSelectedSubCategoryId(sub.Id ?? sub.id)}
                      >
                        {sub.Name ?? sub.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginBottom: "18px" }}>
                <div style={label}>City</div>
                <button
                  onClick={() => setShowCityList((s) => !s)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "11px 14px", borderRadius: "10px", border: "1px solid #e2e8f0",
                    background: "#fff", cursor: "pointer", fontSize: "14px",
                    color: selectedCityId ? "#0f172a" : "#94a3b8", fontWeight: 600,
                  }}
                >
                  <span>📍 {selectedCityId ? cities.find((c) => String(c.Id ?? c.id) === String(selectedCityId))?.Name || cities.find((c) => String(c.Id ?? c.id) === String(selectedCityId))?.name || "Select City" : "Select City"}</span>
                  <span>{showCityList ? "▲" : "▼"}</span>
                </button>

                {showCityList && (
                  <div style={{ marginTop: "8px", border: "1px solid #e2e8f0", borderRadius: "10px", overflow: "hidden" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "9px 12px", borderBottom: "1px solid #f1f5f9" }}>
                      <span style={{ opacity: 0.5 }}>🔍</span>
                      <input
                        value={citySearch}
                        onChange={(e) => setCitySearch(e.target.value)}
                        placeholder="City search karein..."
                        style={{ flex: 1, border: "none", outline: "none", fontSize: "14px" }}
                      />
                    </div>
                    <div style={{ maxHeight: "180px", overflowY: "auto" }}>
                      {filteredCities.map((city) => (
                        <button
                          key={city.Id ?? city.id}
                          onClick={() => { setSelectedCityId(city.Id ?? city.id); setShowCityList(false); }}
                          style={{
                            width: "100%", textAlign: "left", padding: "10px 14px", border: "none",
                            background: String(selectedCityId) === String(city.Id ?? city.id) ? "#eff6ff" : "#fff",
                            cursor: "pointer", fontSize: "14px", color: "#0f172a",
                            display: "flex", justifyContent: "space-between",
                          }}
                        >
                          <span>{city.Name ?? city.name}</span>
                          {String(selectedCityId) === String(city.Id ?? city.id) && <span style={{ color: "#2563eb" }}>✓</span>}
                        </button>
                      ))}
                      {filteredCities.length === 0 && (
                        <div style={{ padding: "14px", color: "#94a3b8", fontSize: "13px", textAlign: "center" }}>
                          Koi city nahi mili
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: "10px", padding: "14px 20px 18px", borderTop: "1px solid #f1f5f9" }}>
          <button onClick={resetAll} style={{ ...btnBase, background: "#f1f5f9", color: "#334155" }}>
            Reset
          </button>
          <button
            onClick={handleApply}
            disabled={!hasActiveFilters()}
            style={{
              ...btnBase,
              background: hasActiveFilters() ? "linear-gradient(135deg,#2563eb,#7c3aed)" : "#e2e8f0",
              color: hasActiveFilters() ? "#fff" : "#94a3b8",
              flex: 2,
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

const label = { fontSize: "13px", fontWeight: 700, color: "#475569", marginBottom: "8px" };
const input = {
  flex: 1, padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f0",
  fontSize: "14px", outline: "none", color: "#0f172a",
};
const btnBase = {
  padding: "12px", borderRadius: "10px", border: "none", fontWeight: 700,
  fontSize: "14px", cursor: "pointer", flex: 1,
};

export { EMPTY_FILTER };
export default FilterModal;
